import { useState } from "react";
import {
  CoreAccordion,
  type HeadingLevel,
  type PropsWithOptionalTestID,
} from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";
import { ChevronDownIcon } from "@boi/react-icons";

import * as S from "./Accordion.styled";
import { Typography } from "../Typography";

export type AccordionProps = PropsWithOptionalTestID & {
  title: string;
  content: JSX.Element;
  isOpen?: boolean;
  initialOpen?: boolean;
  onChange?: (isOpen: boolean) => void;
  variant?: CoreAccordion.Variant;
  headingLevel?: HeadingLevel;
};

function Accordion(props: AccordionProps) {
  const {
    title,
    content,
    isOpen: controlledOpen,
    initialOpen = false,
    onChange: setControlledOpen,
    variant = CoreAccordion.DEFAULT_VARIANT,
    headingLevel = CoreAccordion.DEFAULT_HEADING_LEVEL,
    testID = "accordion",
  } = props;

  const { tokens } = useDesignTokens();
  const { accordion } = tokens;

  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const isExpanded = controlledOpen ?? uncontrolledOpen;
  const handleChange = setControlledOpen ?? setUncontrolledOpen;

  return (
    <S.Container
      isExpanded={isExpanded}
      onExpandedChange={handleChange}
      $variant={variant}
      data-testid={testID}
    >
      <S.Header
        slot="trigger"
        $variant={variant}
        data-testid={`${testID}-header`}
      >
        <S.HeaderInner $variant={variant}>
          <Typography
            color="inherit"
            component={`h${headingLevel}`}
            variant={accordion.header.text.typography.variant[variant]}
            fontFamily={accordion.header.text.typography.fontFamily}
          >
            {title}
          </Typography>

          <S.HeaderIconWrapper $isExpanded={isExpanded}>
            <ChevronDownIcon />
          </S.HeaderIconWrapper>
        </S.HeaderInner>

        <S.Separator $isExpanded={isExpanded} />
      </S.Header>

      <S.Content $isExpanded={isExpanded} data-testid={`${testID}-content`}>
        {content}
      </S.Content>
    </S.Container>
  );
}

export default Accordion;
